// @ts-nocheck
import React, { useState, useEffect } from 'react';
import './SidebarWidgetPreview.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faPaperPlane,
  faRobot,
  faChevronRight,
  faChevronLeft,
  faFileAlt
} from '@fortawesome/free-solid-svg-icons';

interface SidebarWidgetPreviewProps {
  theme: any;
  position?: 'left' | 'right';
}

/**
 * Preview component for the Sidebar Widget in Theme Editor
 * Shows a scaled-down version of the sidebar for customization
 */
const SidebarWidgetPreview: React.FC<SidebarWidgetPreviewProps> = ({
  theme,
  position = 'right'
}) => {
  const [isMinimized, setIsMinimized] = useState(false);

  // Get theme colors
  const themeColors = {
    primary: theme.primaryColor || '#3B82F6',
    secondary: theme.secondaryColor || '#1F2937',
    headerBg: theme.standardHeaderBg || theme.chatContainerGradientStart || theme.primaryColor || '#1e293b',
    headerText: theme.standardHeaderText || theme.chatTextColor || '#ffffff',
    chatBg: theme.standardPanelBg || theme.chatBgColor || '#ffffff',
    userBubble: theme.standardUserBubbleBg || theme.sendButtonBg || theme.primaryColor || '#2563EB',
    userText: theme.standardUserBubbleText || theme.buttonTextColor || '#ffffff',
    botBubble: theme.standardBotBubbleBg || theme.messageResponseBg || '#FFFFFF',
    botText: theme.standardBotBubbleText || theme.chatTextColor || theme.secondaryColor || '#111827',
    inputBg: theme.inputFieldBg || '#F9FAFB',
    inputBorder: theme.inputBorderColor || '#E5E7EB',
    accent: theme.primaryColor || '#3B82F6'
  };

  return (
    <div className="sidebar-preview-container">
      <div className="sidebar-preview-label">
        Sidebar Widget Preview
        <span className="preview-note">
          {position === 'right' ? '→ Right Side' : '← Left Side'}
        </span>
      </div>

      <div className="sidebar-preview-mockup">
        {/* Mock Browser Window */}
        <div className="mockup-browser">
          <div className="mockup-browser-header">
            <div className="mockup-browser-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <div className="mockup-browser-url">yourwebsite.com</div>
          </div>
          <div className="mockup-browser-content">
            <div className="mockup-content-placeholder">
              <div className="placeholder-header"></div>
              <div className="placeholder-text"></div>
              <div className="placeholder-text short"></div>
              <div className="placeholder-text"></div>
            </div>
          </div>
        </div>

        {/* Sidebar Widget */}
        <div
          className={`sidebar-preview ${position === 'left' ? 'sidebar-preview-left' : 'sidebar-preview-right'} ${isMinimized ? 'sidebar-preview-minimized' : ''}`}
          style={{
            '--theme-primary': themeColors.primary,
            '--theme-secondary': themeColors.secondary,
            '--theme-header-bg': themeColors.headerBg,
            '--theme-header-text': themeColors.headerText,
            '--theme-chat-bg': themeColors.chatBg,
            '--theme-user-bubble': themeColors.userBubble,
            '--theme-user-text': themeColors.userText,
            '--theme-bot-bubble': themeColors.botBubble,
            '--theme-bot-text': themeColors.botText,
            '--theme-input-bg': themeColors.inputBg,
            '--theme-input-border': themeColors.inputBorder,
            '--theme-accent': themeColors.accent,
          } as React.CSSProperties}
        >
          {/* Header */}
          <div className="sidebar-preview-header">
            <div className="sidebar-preview-header-content">
              <div className="sidebar-preview-logo">
                <FontAwesomeIcon icon={faRobot} />
                <span>AI Assistant</span>
              </div>
              {!isMinimized && (
                <div className="sidebar-preview-badge">
                  <FontAwesomeIcon icon={faFileAlt} style={{ fontSize: '8px' }} />
                  <span>✓ Ready</span>
                </div>
              )}
            </div>
            <div className="sidebar-preview-actions">
              <button
                className="sidebar-preview-btn"
                onClick={() => setIsMinimized(!isMinimized)}
              >
                <FontAwesomeIcon
                  icon={position === 'right' ? faChevronRight : faChevronLeft}
                  style={{ fontSize: '10px' }}
                />
              </button>
              <button className="sidebar-preview-btn">
                <FontAwesomeIcon icon={faTimes} style={{ fontSize: '10px' }} />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Messages */}
              <div className="sidebar-preview-messages">
                <div className="preview-welcome">
                  <div className="preview-welcome-icon">
                    <FontAwesomeIcon icon={faRobot} />
                  </div>
                  <div className="preview-welcome-title">How can I help?</div>
                  <div className="preview-welcome-text">
                    Ask me anything about this page
                  </div>
                </div>

                <div className="preview-message preview-message-user">
                  <div className="preview-message-content">
                    What is this about?
                  </div>
                </div>

                <div className="preview-message preview-message-assistant">
                  <div className="preview-message-content">
                    I can help answer questions about the page content, products, services, or any information you need!
                  </div>
                </div>
              </div>

              {/* Suggested Questions */}
              <div className="sidebar-preview-suggestions">
                <button className="preview-suggestion">
                  What is this page about?
                </button>
                <button className="preview-suggestion">
                  How can you help me?
                </button>
              </div>

              {/* Input */}
              <div className="sidebar-preview-input">
                <div className="preview-input-container">
                  <input
                    type="text"
                    placeholder="Ask me anything..."
                    className="preview-input"
                    readOnly
                  />
                  <button className="preview-send-btn">
                    <FontAwesomeIcon icon={faPaperPlane} style={{ fontSize: '10px' }} />
                  </button>
                </div>
                <div className="preview-input-hint">
                  Press Enter to send
                </div>
              </div>
            </>
          )}

          {/* Minimized Tab */}
          {isMinimized && (
            <div className="sidebar-preview-tab" onClick={() => setIsMinimized(false)}>
              <FontAwesomeIcon icon={faRobot} style={{ fontSize: '12px' }} />
              <span>Chat</span>
            </div>
          )}
        </div>
      </div>

      <div className="sidebar-preview-info">
        <strong>✨ Next-Gen Design:</strong> Glassmorphism UI, auto page context, inline citations, and smooth animations
      </div>
    </div>
  );
};

export default SidebarWidgetPreview;
